/* @flow */
import { observable, action } from 'mobx';

import { ApiModel } from 'api/ApiModel';

export type TypePropsManufacturersAlias = {
  alias: string,
};

export type TypePropsManufacturers = {
  id: number,
  uuid: string,
  name: string,
  key: string,
  code: string,
  short_code: string,
  global_list: boolean,
  key_oem: number,
  status: string,
  created_at: string,
  updated_at: string,
  deleted_at: null,
  product_approved_count: number,
  product_pending_count: number,
  product_archived_count: number,
  product_not_approved_count: number,
  aliases: TypePropsManufacturersAlias[],
};

export type TypePropsProducts = {
  id: number,
  uuid: string,
  manufacturerId: number,
  name: string,
  productGroupCode: number,
  productGroupDescription: string,
  productGroupKey: string,
  model: string,
  baseModel: string,
  fairMarketValue: number,
  status: string,
  createdAt: string,
  updatedAt: string,
  deletedAt: null,
  totalAssetCount: number,
};

export type TypePropsActivity = {
  activity_reason: boolean,
  admin_id: ?string,
  admin_name: ?string,
  created_at: string,
  id: number,
  key: string,
  new_value: string,
  old_value: string,
  revisionable_id: string,
  revisionable_type: string,
  updated_at: string,
  user_id: string,
  uuser_name: string,
};

export default class ManufacturersProductsStore extends ApiModel {
  @observable manufacturers: Array<TypePropsManufacturers> = [];

  @observable products: Array<TypePropsProducts> = [];

  @observable selectedManufacturer: *;

  @observable selectedProduct: *;

  @observable selectedManufacturerId: ?number = null;

  @observable selectedProductId: ?number = null;

  @observable selectedProductStatus: ?string = null;

  @observable selectedProductTitle: string = 'Products';

  @observable selectedActiveListIndex: ?number = null;

  @observable loadingManufacturers: boolean = false;

  @observable loadingSpecificManufacturer: boolean = false;

  @observable loadingProducts: boolean = false;

  @observable loadingSelectedProduct: boolean = false;

  @observable productActivity: ?Array<*> = null;

  @observable manufacturerActivity: ?Array<*> = null;

  @action async loadManufacturers(): Promise<void> {
    this.resetStore();
    this.loadingManufacturers = true;
    this.manufacturers = await this.api.getResource('manufacturers').catch((err: string) => {
      console.log(err);
      return [];
    });
    this.loadingManufacturers = false;
  }

  @action async loadSelectedManufacturer(): Promise<void> {
    this.loadingSpecificManufacturer = true;
    this.selectedManufacturer = await this.api
      .getResource(`manufacturers/${String(this.selectedManufacturerId)}`)
      .catch((err: string) => {
        console.log(err);
        return [];
      });
    this.loadingSpecificManufacturer = false;
  }

  @action async fetchSelectedProducts(productTitle: string): Promise<void> {
    this.loadingProducts = true;
    this.selectedProductTitle = 'Products';
    this.products = await this.api
      .getResource(
        `products/manufacturers/${String(this.selectedManufacturerId)}?search=${
          this.selectedProductStatus
        }&searchFields=status` // eslint-disable-line
      )
      .catch((err: string) => {
        console.log(err);
        return [];
      });
    this.selectedProductTitle = productTitle;
    this.loadingProducts = false;
  }

  @action async loadSelectedProduct(): Promise<void> {
    this.loadingSelectedProduct = true;
    this.selectedProduct = await this.api.getResource(`products/${String(this.selectedProductId)}`);
    this.loadingSelectedProduct = false;
  }

  @action async getProductActivity(): Promise<void> {
    this.loadingSelectedProduct = true;

    // const res: ?Array<*> =
    await this.api.getResource(`products/${String(this.selectedProductId)}/activity`);

    this.loadingSelectedProduct = false;
  }

  @action async getManufacturerActivity(): Promise<void> {
    this.loadingSelectedProduct = true;

    // const res: ?Array<*> =
    await this.api.getResource(`manufacturers/${String(this.selectedManufacturerId)}/activity`);

    this.loadingSelectedProduct = false;
  }

  @action setSelectedManufacturerId(id: number) {
    this.resetProducts();
    this.selectedManufacturer = null;
    this.selectedManufacturerId = id;
    this.manufacturerActivity = null;
    this.loadSelectedManufacturer();
  }

  @action setSelectedProductId(id: number) {
    this.selectedProduct = null;
    this.selectedProductId = id;
    this.productActivity = null;
    this.loadSelectedProduct();
  }

  @action setSelectedProductStatus(productStatus: string, productTitle: string) {
    this.selectedProductStatus = productStatus;
    this.selectedActiveListIndex = null;
    this.selectedProductId = null;
    this.selectedProduct = null;
    this.products = [];
    this.fetchSelectedProducts(productTitle);
  }

  @action setSelectedProductStatusEmpty() {
    this.selectedProductStatus = 'Empty';
    this.selectedProductId = null;
    this.products = [];
  }

  @action resetManufacturers() {
    this.manufacturers = [];
    this.selectedManufacturer = null;
    this.selectedManufacturerId = null;
    this.loadingManufacturers = false;
    this.loadingSpecificManufacturer = false;
    this.manufacturerActivity = null;
  }

  @action resetProducts() {
    this.products = [];
    this.selectedProductTitle = 'Products';
    this.selectedProductId = null;
    this.selectedProduct = null;
    this.selectedProductStatus = null;
    this.loadingProducts = false;
    this.loadingSelectedProduct = false;
    this.productActivity = null;
  }

  @action resetStore() {
    this.resetManufacturers();
    this.resetProducts();
  }

  filterByProductStatus = (data: Object) => {
    return data.status === this.selectedProductStatus;
  };
}

export type TypeManufacturersProductsStore = {|
  manufacturers: Array<TypePropsManufacturers>,
  products: Array<TypePropsProducts>,
  productActivity: *,
  manufacturerActivity: *,
  selectedManufacturer: TypePropsManufacturers,
  selectedProduct: *,
  selectedManufacturerId: number,
  selectedProductId: number,
  selectedProductStatus: string,
  selectedActiveListIndex: ?number,
  loadingManufacturers: boolean,
  loadingSpecificManufacturer: boolean,
  loadingProducts: boolean,
  loadingSelectedProduct: boolean,
  loadManufacturers: () => {},
  loadSelectedManufacturer: () => {},
  fetchSelectedProducts: () => {},
  loadSelectedProduct: () => {},
  setSelectedManufacturerId: (id: number) => {},
  setSelectedProductId: (id: number) => {},
  setSelectedProductStatus: (productStatus: string, productTitle: string) => {},
  setSelectedProductStatusEmpty: () => {},
  resetStore: () => {},
  getProductActivity: () => {},
  getManufacturerActivity: () => {},
|};
